import React, { useEffect, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  ApiError,
  FreigabeAuftrag,
  getSelbstFreigabe,
  isHotMode,
  selbstFreigabe,
  stornoSelbstFreigabe,
  ZuepStatusInfo,
} from './ApiClient';
import { RENEWAL_WINDOW_DAYS } from './tileGating';

function fmtDate(d?: string | null): string {
  if (!d) return '–';
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
}

const boxStyle: React.CSSProperties = {
  maxWidth: 640,
  margin: '10px auto 0',
  padding: '14px 18px',
  borderRadius: 10,
  textAlign: 'left',
  fontSize: 14,
  lineHeight: 1.45,
  background: '#e7f5ec',
  color: '#1e5631',
  border: '1px solid #b7e1c1',
};

/**
 * Ein-Klick-Verlängerung ("Selbst-Freigabe"): Wenn sich bei Wohnsitz/Arbeit
 * nichts geändert hat und alle Dokumente gültig sind, bestätigt das Mitglied
 * hier drei Punkte — das System plant die Freigabe automatisch im optimalen
 * Fenster (28-40 Tage vor Ablauf) und übermittelt sie zur nächsten
 * Behörden-Öffnungszeit. Alle Guards laufen server-seitig.
 */
function SelbstFreigabeCard({ zuepStatus }: { zuepStatus?: ZuepStatusInfo | null }) {
  const { keycloak } = useKeycloak();
  const [auftrag, setAuftrag] = useState<FreigabeAuftrag | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [cUnveraendert, setCUnveraendert] = useState(false);
  const [cRichtig, setCRichtig] = useState(false);
  const [cKosten, setCKosten] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSelbstFreigabe(keycloak.token)
      .then((r) => {
        if (!cancelled) {
          setAuftrag(r.auftrag);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [keycloak.token]);

  if (!loaded || zuepStatus?.code !== 'zuverlaessig') return null;

  // Verfügbar ab RENEWAL_WINDOW_DAYS (60) Tagen vor Ablauf — derselbe
  // Zeitpunkt, zu dem die Verlängerungs-Kachel öffnet und die Einladungs-
  // Mail rausgeht; der Server erzwingt dieselbe Grenze. Frühe Bestätigung
  // ist gefahrlos: der Auftrag wartet server-seitig bis zum Behörden-
  // Sweet-Spot (earliest_at). Hot-Modus übersteuert.
  const daysLeft = zuepStatus.frist
    ? Math.ceil((new Date(`${zuepStatus.frist}T00:00:00`).getTime() - Date.now()) / 86_400_000)
    : null;
  const imFenster = daysLeft !== null && daysLeft <= RENEWAL_WINDOW_DAYS;
  if (!auftrag && !imFenster && !isHotMode()) return null;

  // Bereits beauftragt: Status + Storno.
  if (auftrag) {
    return (
      <div style={boxStyle}>
        {auftrag.status === 'wartet_admin' ? (
          <>
            ✅ <strong>Dein Auftrag ist eingegangen.</strong> Die Freigabe wird ab dem{' '}
            <strong>{fmtDate(auftrag.earliest_at)}</strong> automatisch an die Behörde übermittelt.
            Du musst nichts weiter tun.
          </>
        ) : (
          <>
            ✅ <strong>Deine Verlängerung ist eingeplant.</strong> Die Freigabe wird ab dem{' '}
            <strong>{fmtDate(auftrag.earliest_at)}</strong> automatisch an die Behörde übermittelt
            (Behörden-Öffnungszeiten werden berücksichtigt). Du musst nichts weiter tun — wir
            informieren dich, sobald die Behörde entschieden hat.
          </>
        )}
        <div style={{ marginTop: 8 }}>
          <button
            type='button'
            style={{ padding: '3px 12px', borderRadius: 6, border: '1px solid #1e5631', background: 'transparent', color: 'inherit', fontSize: 13, cursor: 'pointer' }}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await stornoSelbstFreigabe(keycloak.token);
                setAuftrag(null);
                setMsg(null);
              } catch (err) {
                setMsg({ ok: false, text: 'Storno fehlgeschlagen: ' + (err as Error).message });
              } finally {
                setBusy(false);
              }
            }}
          >
            Planung stornieren
          </button>
        </div>
        {msg && <div style={{ marginTop: 6, color: msg.ok ? undefined : '#c0392b' }}>{msg.text}</div>}
      </div>
    );
  }

  return (
    <div style={boxStyle}>
      ⚡ <strong>Nichts geändert? Ein-Klick-Verlängerung.</strong>{' '}
      Wenn Wohnsitz und Arbeit gleich geblieben sind und deine Dokumente gültig sind,
      kannst du deine ZÜP-Verlängerung hier direkt beauftragen — das System übermittelt
      die Freigabe automatisch zum richtigen Zeitpunkt.
      {!expanded ? (
        <div style={{ marginTop: 8 }}>
          <button
            type='button'
            style={{ padding: '4px 14px', borderRadius: 6, border: 'none', background: '#1e7e34', color: '#fff', fontSize: 14, cursor: 'pointer' }}
            onClick={() => setExpanded(true)}
          >
            Verlängerung beauftragen
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            💶 <strong>Kosten:</strong> Für die Bearbeitung fallen Gebühren laut{' '}
            <a href='https://www.innsbruck-airport.com/business-aviation/aviation/entgeltordnung-zfbb' target='_blank' rel='noreferrer'>Entgeltordnung des Flughafens Innsbruck</a>{' '}
            an; die Abrechnung läuft über den Verein.
          </div>
          <label style={{ display: 'block', marginBottom: 4 }}>
            <input type='checkbox' checked={cUnveraendert} onChange={(e) => setCUnveraendert(e.target.checked)} />{' '}
            Bei Wohnsitz und Beschäftigung hat sich seit meiner letzten ZÜP <strong>nichts geändert</strong>.
          </label>
          <label style={{ display: 'block', marginBottom: 4 }}>
            <input type='checkbox' checked={cRichtig} onChange={(e) => setCRichtig(e.target.checked)} />{' '}
            Meine hinterlegten Angaben sind <strong>vollständig und richtig</strong>.
          </label>
          <label style={{ display: 'block', marginBottom: 8 }}>
            <input type='checkbox' checked={cKosten} onChange={(e) => setCKosten(e.target.checked)} />{' '}
            Mir ist bewusst, dass mit der Freigabe <strong>Kosten ausgelöst</strong> werden.
          </label>
          <button
            type='button'
            style={{ padding: '4px 14px', borderRadius: 6, border: 'none', background: cUnveraendert && cRichtig && cKosten ? '#1e7e34' : '#9aa0a6', color: '#fff', fontSize: 14, cursor: 'pointer' }}
            disabled={!cUnveraendert || !cRichtig || !cKosten || busy}
            onClick={async () => {
              setBusy(true);
              setMsg(null);
              try {
                const r = await selbstFreigabe(keycloak.token, {
                  unveraendert: cUnveraendert,
                  richtig: cRichtig,
                  kosten: cKosten,
                });
                setAuftrag({
                  id: r.id,
                  mgd_nummer: '',
                  earliest_at: r.geplant_fuer,
                  status: (r as any).status || 'wartet_admin',
                });
              } catch (err) {
                const text = err instanceof ApiError ? err.message : (err as Error).message;
                setMsg({ ok: false, text });
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Wird geplant…' : 'Verbindlich beauftragen'}
          </button>
          {msg && <div style={{ marginTop: 6, color: '#c0392b' }}>{msg.text}</div>}
        </div>
      )}
    </div>
  );
}

export default SelbstFreigabeCard;
